import { CheckSquare, MoreVertical } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import { toast } from "react-toastify";
import routing from "routing";
import type { DeckCategoryFilter } from "src/api/flashcardDecks";
import { Menu } from "src/components/Default/LekUI/Menu";
import type { SortOption } from "src/components/Default/LekUI/SortButton";
import { SortButton } from "src/components/Default/LekUI/SortButton";
import { DecksLearnSelectionBar } from "src/components/SelectionBar/DecksLearnSelectionBar";
import { useSlot } from "src/components/Slots/SlotsContext";
import { getMaxDecksInBatch } from "src/hooks/useAppConfig";
import { useDecksSearch } from "src/hooks/useDecksSearch";
import type { DeckSortBy, DeckSortOrder } from "src/hooks/useFlashcards";
import { useCopyAndUseDecks } from "src/hooks/useFlashcards";
import { useSearchFiltersState } from "src/hooks/useSearchFiltersState";
import { useSearchPage } from "src/hooks/useSearchPage";
import { DecksSearchGrid } from "./DecksViews/DecksSearchGrid";
import { DefaultSearch } from "./DefaultSearch";
import { DecksFilters } from "./Filters/DecksFilters/DecksFilters";
import { SearchEmptyState } from "./SearchEmptyState";

// author = kategoria z availableCategories (all/admin/users/essential)
const defaultDetails = {
	author: "all" as DeckCategoryFilter,
	searchValue: "",
};

type DecksDetails = Record<string, unknown> & typeof defaultDetails;

const SORT_OPTIONS: SortOption<DeckSortBy, DeckSortOrder>[] = [
	{
		orderBy: "popularity",
		order: "desc",
		labelKey: "decks.sort.popularity",
		labelFallback: "Popularność",
	},
	{
		orderBy: "createdAt",
		order: "desc",
		labelKey: "decks.sort.createdAt",
		labelFallback: "Data utworzenia",
	},
	{
		orderBy: "createdAt",
		order: "asc",
		labelKey: "decks.sort.createdAt",
		labelFallback: "Data utworzenia",
	},
	{
		orderBy: "name",
		order: "asc",
		labelKey: "decks.sort.alphabetical",
		labelFallback: "Alfabetycznie",
	},
	{
		orderBy: "name",
		order: "desc",
		labelKey: "decks.sort.alphabetical",
		labelFallback: "Alfabetycznie",
	},
];

// wyszukiwanie, filtr autora i sortowanie po stronie backendu (v2 /search?type=decks)
export function DecksSearch() {
	const { t } = useTranslation();
	const history = useHistory();
	const [page, setPage] = useSearchPage();
	const [selectedSort, setSelectedSort] = useState(0);
	const [isSelectMode, setIsSelectMode] = useState(false);
	const [selectedIds, setSelectedIds] = useState<number[]>([]);
	const maxDecksInBatch = getMaxDecksInBatch();
	const { mutateAsync: learnFromDecks, isPending: isLearning } =
		useCopyAndUseDecks();
	const [details, setDetails] =
		useSearchFiltersState<DecksDetails>(defaultDetails);
	const currentSort = SORT_OPTIONS[selectedSort];
	const ActionsSlot = useSlot("decksSearchActions");

	const { data, isLoading, isFetching } = useDecksSearch(details.searchValue, {
		page,
		orderBy: currentSort.orderBy,
		order: currentSort.order,
		category: details.author !== "all" ? details.author : undefined,
	});

	const categoryLabels = Object.fromEntries(
		(data?.availableCategories ?? []).map(({ value, label }) => [value, label]),
	);

	const handleSetDetails = (value: Record<string, unknown>) => {
		setPage(0);
		setDetails(value);
	};

	const handleSortChange = (index: number) => {
		setPage(0);
		setSelectedSort(index);
	};

	const exitSelectMode = () => {
		setIsSelectMode(false);
		setSelectedIds([]);
	};

	const warnMaxSelected = () => {
		toast.warning(
			t(
				"decks.search.maxSelected",
				"Możesz wybrać maksymalnie {{count}} zestawów",
				{
					count: maxDecksInBatch,
				},
			),
			{
				position: toast.POSITION.BOTTOM_LEFT,
				pauseOnHover: false,
				autoClose: 3000,
			},
		);
	};

	const toggleSelect = (id: number) => {
		if (selectedIds.includes(id)) {
			setSelectedIds((prev) => prev.filter((i) => i !== id));
			return;
		}
		if (selectedIds.length >= maxDecksInBatch) {
			warnMaxSelected();
			return;
		}
		setSelectedIds((prev) => [...prev, id]);
	};

	const toggleInList = (id: number) => {
		if (selectedIds.includes(id)) {
			setSelectedIds((prev) => prev.filter((i) => i !== id));
			return;
		}
		if (selectedIds.length >= maxDecksInBatch) {
			warnMaxSelected();
			return;
		}
		setIsSelectMode(true);
		setSelectedIds((prev) => [...prev, id]);
	};

	const handleLearn = async () => {
		if (!selectedIds.length || selectedIds.length > maxDecksInBatch) {
			return;
		}
		try {
			await learnFromDecks({ deckIds: selectedIds });
			exitSelectMode();
			toast.success(
				t("decks.search.learnSuccess", "Zestawy dodane do Twojej biblioteki"),
				{
					position: toast.POSITION.BOTTOM_LEFT,
					pauseOnHover: false,
					autoClose: 3000,
				},
			);
			history.push(routing.paths.userDecks);
		} catch {
			toast.error(t("errors.somethingWentWrong", "Coś poszło nie tak"), {
				position: toast.POSITION.BOTTOM_LEFT,
				pauseOnHover: false,
				autoClose: 3000,
			});
		}
	};

	const menuItems = [
		{
			title: t("folder.selectMany", "Zaznacz wiele"),
			onClick: () => setIsSelectMode(true),
			Icon: <CheckSquare className="icon-small" />,
			testId: "decks-menu-select-many-item",
		},
	];

	return (
		<DefaultSearch
			isLoading={isFetching && !isLoading}
			tabsActions={
				<div className="flex items-center gap-8 flex-shrink-0">
					{ActionsSlot && <ActionsSlot />}
					<SortButton
						options={SORT_OPTIONS}
						selectedIndex={selectedSort}
						onSelect={handleSortChange}
						testId="decks-sort-button"
						containerName="search-view-container"
					/>
					<Menu
						icon={<MoreVertical className="icon-small" />}
						buttonClassName="folder-menu-button"
						buttonTestId="decks-actions-menu-button"
						anchorId="decks-actions"
						closeAfterClick
						items={menuItems}
					/>
				</div>
			}
			renderView={({ scrollToTop, clearFilters }) => (
				<div className="folders-items-list-container flex flex-col gap-16">
					{isSelectMode && (
						<DecksLearnSelectionBar
							selectedCount={selectedIds.length}
							onLearn={handleLearn}
							onClose={exitSelectMode}
							isLearning={isLearning}
						/>
					)}
					<DecksSearchGrid
						decks={data?.items ?? []}
						isLoading={isLoading}
						pagesCount={data?.totalPages ?? 0}
						page={page}
						setPage={setPage}
						scrollToTop={scrollToTop}
						isSelectMode={isSelectMode}
						selectedIds={selectedIds}
						onToggleSelect={toggleSelect}
						onToggleList={toggleInList}
						categoryLabels={categoryLabels}
						emptyState={
							<SearchEmptyState
								type="decks"
								searchValue={details.searchValue}
								onClearFilters={clearFilters}
							/>
						}
					/>
				</div>
			)}
			details={details}
			setDetails={handleSetDetails}
			filters={(draft, setDraft) => (
				<DecksFilters
					details={draft}
					setDetails={setDraft}
					categories={data?.availableCategories}
				/>
			)}
			defaultDetails={defaultDetails}
			filterLabels={categoryLabels}
			count={data?.totalCount}
			counts={data?.counts}
			activeFilter="decks"
		/>
	);
}
